import React from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Button from '@/components/ui/Button';
import axios from 'axios';
import * as yup from 'yup';
import {Formik, FormikProps} from 'formik';
import {useStoreActions, useStoreState} from 'easy-peasy';
import Select from 'react-select';
import {navigate} from '@reach/router';

declare global {
    interface Window {
        URLS: Record<string, string>;
        ROUTES: Record<string, string>;
    }
}

import CellLoader from '@/components/loading/CellLoader';
import useData from '@/utils/useData';
import ActionSendEmailFormFields from './action_form_fieldsets/ActionSendEmailFormFields';
import ActionReplyEmailFormFields from './action_form_fieldsets/ActionReplyEmailFormFields';
import ActionRerouteEmailsFormFields from './action_form_fieldsets/ActionRerouteEmailsFormFields';
import ActionWebhookFormFields from './action_form_fieldsets/ActionWebhookFormFields';
import ActionAssignAgentFormFields from './action_form_fieldsets/ActionAssignAgentFormFields';
import ActionAssignTeamFormFields from './action_form_fieldsets/ActionAssignTeamFormFields';
import i18n from 'i18n';
import {isBackendValidEmail} from '@/utils/emailValidator';

interface ActionTypeChoice {
    value: string;
    label: string;
}

interface ActionParams {
    emailArray: string[];
    url?: string;
    title?: string;
    description?: string;
    replyMessage?: string;
    timeout?: string;
    accountId?: number;
    folderPath?: string[];
    agentId?: number;
    teamId?: number;
}

interface Action {
    id: number;
    title: string;
    actionTypeIdentifier: string;
    params: ActionParams;
    channelId?: number;
}

interface FormValues {
    title: string;
    emailSelectInputValue: string;
    actionTypeIdentifier: string;
    params: ActionParams;
    channelId?: number;
}

interface ActionSettingsFormProps {
    actionId?: number;
    channelId?: number;
    onDone: () => void;
}

const actionFormSchemaFactory = (paramsSchema: any) => yup.object({
    title: yup.string()
        .max(64, i18n.t('jt_action_title_max.text'))
        .required(i18n.t('jt_action_title_required.text')),
    actionTypeIdentifier: yup.string().required(),
    params: yup.object().shape(paramsSchema),
});

const actionFormSchema = yup.lazy((values: FormValues) => {
    switch (values.actionTypeIdentifier) {
        case 'action_email_send_params':
            return actionFormSchemaFactory({
                emailArray: yup.array().of(
                    yup.string().test(
                        'backend-email',
                        ({value}) => i18n.t('jt_action_invalid_email.text', {value}),
                        (value) => !!value && isBackendValidEmail(value),
                    ),
                ).required(i18n.t('jt_action_email_required.text')),
            });
        case 'action_reroute_emails_params':
            return actionFormSchemaFactory({
                accountId: yup.number().required(i18n.t('jt_action_account_required.text')),
                folderPath: yup.array().of(
                    yup.string()
                        .max(255, i18n.t('jt_action_folder_max.text'))
                        .required(i18n.t('jt_action_folder_empty.text')),
                ).required(i18n.t('jt_action_folder_required.text')),
            });
        case 'action_email_reply_params':
            return actionFormSchemaFactory({
                replyMessage: yup.string(),
            });
        case 'action_webhook_params':
            return actionFormSchemaFactory({
                url: yup.string()
                    .url(i18n.t('jt_action_url_invalid.text'))
                    .required(i18n.t('jt_action_url_required.text')),
            });
        case 'action_task_assignment_agent_params':
            return actionFormSchemaFactory({
                title: yup.string()
                    .required(i18n.t('jt_action_task_title_required.text'))
                    .max(64, i18n.t('jt_action_task_title_max.text')),
                description: yup.string(),
                timeout: yup.string(),
                agentId: yup.number().required(i18n.t('jt_action_agent_required.text')),
            });
        case 'action_task_assignment_team_params':
            return actionFormSchemaFactory({
                title: yup.string()
                    .required(i18n.t('jt_action_task_title_required.text'))
                    .max(64, i18n.t('jt_action_task_title_max.text')),
                description: yup.string(),
                timeout: yup.string(),
                teamId: yup.number().required(i18n.t('jt_action_team_required.text')),
            });
        default:
            return yup.object();
    }
});

const ActionSettingsForm: React.FC<ActionSettingsFormProps> = React.memo((
    {
        actionId = 0,
        channelId = undefined,
        onDone,
    },
) => {
    const actionsList = useStoreState((state: any) => state.actions.items);
    const [actionTypeChoices, actionsLoading] = useData<ActionTypeChoice[]>(window.URLS.actionTypeChoicesResource, {
        storeResolver: 'actions',
        actionResolver: 'loadActionTypeChoices',
        dataResolver: 'actionTypeChoices',
    });

    const [isTimeoutValid, setIsTimeoutValid] = React.useState<boolean>(true);
    const addOrEditAction = useStoreActions((actions: any) => actions.actions.addOrEditAction);
    const setReloadAction = useStoreActions((actions: any) => actions.actions.setReload);

    const onSubmit = async (values: FormValues): Promise<void> => {
        let requestMethod: (url: string, data: any) => Promise<any>;
        let url = window.URLS.actionsResource;

        if (actionId) {
            url = `${url}/${actionId}`;
            requestMethod = axios.put;
        } else {
            requestMethod = axios.post;
        }

        const response = await requestMethod(url, {
            ...values,
            channelId,
        });

        onDone();
        addOrEditAction(response.data);
        setReloadAction(true);

        if (!actionId) {
            if (channelId) {
                await navigate(`/admin/channels/${channelId}/rules?addrule=${response.data.id}`);
            } else {
                await navigate(`${window.ROUTES.rulesViewRoute}?addrule=${response.data.id}`);
            }
        }
    };

    const removeEmail = (formik: FormikProps<FormValues>, email: string): void => {
        const emailIndex = formik.values.params.emailArray?.indexOf(email);
        if (emailIndex && emailIndex > -1) {
            const newEmailArray = [...(formik.values.params.emailArray || [])];
            newEmailArray.splice(emailIndex, 1);
            formik.setFieldValue('params.emailArray', newEmailArray);
        } else {
            console.error('You tried to remove an email that doesn\'t exist. Sure seems like a bug?');
        }
    };

    const addEmail = (formik: FormikProps<FormValues>, newEmail: string): void => {
        if (!newEmail) {
            return;
        }

        const emailArray = formik.values.params.emailArray || [];
        if (!emailArray.includes(newEmail)) {
            formik.setFieldValue(
                'params.emailArray',
                emailArray.concat([newEmail]),
            );
        } else {
            removeEmail(formik, newEmail);
            addEmail(formik, newEmail);
        }
        formik.setFieldValue('emailSelectInputValue', '');
    };

    if (actionsLoading) {
        return (
            <CellLoader fullHeight={true} />
        );
    }

    return (
        <Formik
            initialValues={actionsList.find((action: Action) => action.id === actionId) || {
                title: '',
                emailSelectInputValue: '',
                actionTypeIdentifier: actionTypeChoices[0]?.value || '',
                params: {
                    emailArray: [],
                    url: '',
                    title: '',
                    description: '',
                    replyMessage: '',
                    timeout: '',
                    accountId: 0,
                    folderPath: [''],
                },
                channelId,
            }}
            onSubmit={onSubmit}
            validationSchema={actionFormSchema}
        >
            {(formik: FormikProps<FormValues>) => (
                <Form noValidate onSubmit={formik.handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="actionTypeIdentifier" >
                                <Form.Label>
                                    {i18n.t('jt_action_type.text')}
                                </Form.Label>
                                <Select
                                    value={actionTypeChoices.find((x) => x.value === formik.values.actionTypeIdentifier)}
                                    onChange={(v: ActionTypeChoice | null) => formik.setFieldValue('actionTypeIdentifier', v?.value || '')}
                                    onBlur={formik.handleBlur}
                                    name="actionTypeIdentifier"
                                    isSearchable={false}
                                    options={actionTypeChoices}
                                    isDisabled={!!actionId}
                                    aria-label="select-action-type"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="actionTitle" >
                                <Form.Label>
                                    {i18n.t('jt_action_name.text')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.title}
                                    isInvalid={formik.touched.title && !!formik.errors.title}
                                    autoFocus
                                    data-cy="action-name-input"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.title}
                                </Form.Control.Feedback>
                            </Form.Group>

                        </Col>
                    </Row>

                    {
                        formik.values.actionTypeIdentifier === 'action_email_send_params' ? (
                            <ActionSendEmailFormFields
                                formik={formik as any}
                                addEmail={addEmail as any}
                            />
                        ) : null
                    }

                    {
                        formik.values.actionTypeIdentifier === 'action_email_reply_params' ? (
                            <ActionReplyEmailFormFields formik={formik} />
                        ) : null
                    }


                    {
                        formik.values.actionTypeIdentifier === 'action_reroute_emails_params' ? (
                            <ActionRerouteEmailsFormFields formik={formik as any} />
                        ) : null
                    }


                    {
                        formik.values.actionTypeIdentifier === 'action_webhook_params' ? (
                            <ActionWebhookFormFields formik={formik} />
                        ) : null
                    }

                    {
                        formik.values.actionTypeIdentifier === 'action_task_assignment_agent_params' ? (
                            <ActionAssignAgentFormFields formik={formik as any} setTimeoutValidity={setIsTimeoutValid as any} />
                        ) : null
                    }

                    {
                        formik.values.actionTypeIdentifier === 'action_task_assignment_team_params' ? (
                            <ActionAssignTeamFormFields formik={formik as any} setTimeoutValidity={setIsTimeoutValid as any} />
                        ) : null
                    }

                    <Row>
                        <Form.Group className="ml-auto mb-3" controlId="action-save">
                            <Button
                                variant="info"
                                type="submit"
                                data-cy="action-save-btn"
                                disabled={!isTimeoutValid}
                            >
                                {i18n.t('jt_save.text')}
                            </Button>
                        </Form.Group>
                    </Row>
                </Form >
            )}
        </Formik >
    );
});


export default ActionSettingsForm; 